import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import type { ShotData, ShotFeed } from '../../../stores/types/shots';
import { FEED_TYPES } from '../../../stores/reducers/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import Socket from '../../../connection/socket';
import useShots from '../../../contexts/shots/provider';
import VideoFeedThumbnail from '../video-feed-thumbnail';
import Thumbnail from '../../chat/chat-document-thumbnail';
import { getCurrentPageThumb } from '../../../utils/media';
import {
	AUDIO_TYPES,
	DOC_TYPES,
	IMG_TYPES,
	VID_TYPES,
} from '../../../types/constants';
import EmptyScreenShare from '../../main-video/empty-screen-share';
import type { Profile } from '../../../stores/types/invitations';
import VideoFeedFakeThumbnail from '../video-feed-fake-thumbnail';
import AudioOnlyFeedsIcon from '../../general-components/audio-only-feeds-icon';
import VideoThumbnail from '../../main-video/video-thumbnail';
import AudioThumbnail from '../../media/audio-thumbnail';
import SrtStreamThumbnail from '../../main-video/srt-stream/srt-stream-thumbnail';
import { ThumbnailLocation } from '../../../types/enums';
import OnsiteLocationThumbnail from '../../main-video/onsite-location/onsite-location-thumbnail';
import { selectSrtStreams } from '../../../stores/selectors/video';
import { SrtStreamStatus } from '../../../stores/types/video';

interface ShotDrawerProps {
	shotData: ShotData;
	shotId: number;
	socket: Socket | null;
}

export default function ShotDrawerVideoThumbs(props: ShotDrawerProps) {
	const { shotData, shotId, socket } = props;

	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const { standaloneSrtStreams, onsiteSrtStreams } = useSelector(selectSrtStreams);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const onsiteLocationStreams = useSelector((s: GS) => s.VideoReducer.onsiteLocationStreams);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);

	const { currentShot } = useShots();

	const currentShotId = currentShot.shot_id;

	const {
		shotFeeds,
	} = shotData;

	const isCurrentActiveShot = useMemo(
		() => currentShotId === shotId,
		[currentShotId, shotId],
	);

	const mediaOnCanvasIds = useMemo((): (number | string)[] => {
		const mediaIds: (number | string)[] = [];
		if (currentShot?.shot_data?.shotFeeds) {
			currentShot.shot_data.shotFeeds.forEach((feed) => {
				if (feed.media) {
					mediaIds.push(feed.media);
				} else if (feed.screenshare) {
					mediaIds.push(feed.screenshare);
				} else if (feed.presenter) {
					mediaIds.push(feed.presenter);
				}
			});
		}
		return mediaIds;
	}, [currentShot.shot_data.shotFeeds]);

	const presenterIds = shotFeeds.filter(
		(sf) => sf.feedType === FEED_TYPES.PRESENTER || sf.feedType === FEED_TYPES.SCREENSHARE,
	).map((sf) => sf.presenter ?? sf.screenshare);

	const sipStreamIds = shotFeeds.filter(
		(sf) => sf.feedType === FEED_TYPES.SIP_CALL,
	).map((sf) => sf.sipStreamId);

	const connectedPresentersInShot = connectedPresenterVideos.filter(
		(cpv) => presenterIds.includes(cpv.profile ?? 0),
	);

	const connectedSipStreamsInShot = connectedPresenterVideos.filter(
		(cpv) => sipStreamIds.includes(cpv.id),
	);

	const getAOFIcon = (shotFeed: ShotFeed) => (
		<AudioOnlyFeedsIcon
			isCurrentActiveShot={isCurrentActiveShot}
			mediaOnCanvasIds={mediaOnCanvasIds}
			shotFeed={shotFeed}
		/>
	);

	const getSipCallThumb = useCallback((shotFeed: ShotFeed) => {
		const sipStream = connectedSipStreamsInShot.find(
			(c) => c.id === shotFeed.sipStreamId,
		);
		if (sipStream) {
			return (
				<div className='shot-drawer-thumbnail' key={sipStream.id}>
					<div className="video-thumb">
						<VideoFeedThumbnail
							video={sipStream}
							socket={socket}
						/>
						{shotFeed.audioOnly && getAOFIcon(shotFeed)}
					</div>
				</div>
			);
		}
		console.log('no matching sip stream found for => ', shotFeed);

		return null;
	}, [connectedSipStreamsInShot]);

	const getPresenterThumb = useCallback((shotFeed: ShotFeed) => {
		const presenterInShot = connectedPresentersInShot.find(
			(c) => c.profile === shotFeed.presenter,
		);
		const invitation = invitations.find((i) => i.profile.gr_user === shotFeed.presenter);
		if (presenterInShot) {
			return (
				<div className='shot-drawer-thumbnail' key={presenterInShot.profile}>
					<div className={classNames(
						'video-thumb',
						{ 'has-aof-icon': shotFeed.audioOnly },
					)
					}>
						<VideoFeedThumbnail
							video={presenterInShot}
							socket={socket}
						/>
						{shotFeed.audioOnly && getAOFIcon(shotFeed)}
					</div>
				</div>
			);
		} if (invitation) {
			return (
				<div className='shot-drawer-thumbnail' key={invitation.code}>
					<div className={classNames(
						'video-thumb',
						{ 'has-aof-icon': shotFeed.audioOnly },
					)
					}>
						<VideoFeedFakeThumbnail
							invite={invitation}
							key={invitation.code}
						/>
						{shotFeed.audioOnly && getAOFIcon(shotFeed)}
					</div>
				</div>
			);
		}
		console.log('no matching presenter found for => ', shotFeed);

		return null;
	}, [
		connectedPresentersInShot,
		invitations,
		isCurrentActiveShot,
		mediaOnCanvasIds,
	]);

	const getScreenshareThumb = useCallback((shotFeed: ShotFeed) => {
		const user = connectedUsers.find((u) => u.email === shotFeed.screenshare);
		const invitation = invitations.find((i) => i.profile.email === shotFeed.screenshare);
		const screenshare = connectedPresenterVideos.find(
			(c) => c.id === user?.screenShareWebrtcId,
		);
		if (screenshare) {
			return (
				<div className='shot-drawer-thumbnail'>
					<div className="video-thumb">
						<VideoFeedThumbnail
							video={screenshare}
							socket={socket}
						/>
						{shotFeed.audioOnly && getAOFIcon(shotFeed)}
					</div>
				</div>
			);
		} if (user || invitation) {
			return (
				<div className='shot-drawer-thumbnail'>
					<div className="video-thumb">
						<VideoThumbnail
							key={shotFeed.screenshare}
							includeSelectionBox
							isCurrentActiveShot={isCurrentActiveShot}
							mediaOnCanvasIds={mediaOnCanvasIds}
							shotFeed={shotFeed}
						>
							<EmptyScreenShare
								grUserId={user?.gr_user ?? invitation?.profile?.gr_user ?? 0}
								size={26}
							/>
						</VideoThumbnail>
					</div>
				</div>
			);
		}
		console.log('no matching screenshare found for => ', shotFeed);

		return null;
	}, [
		connectedUsers,
		connectedPresenterVideos,
		invitations,
		isCurrentActiveShot,
		mediaOnCanvasIds,
	]);

	const getMediaThumb = useCallback((shotFeed: ShotFeed) => {
		const mediaItem = media.find((m) => m.id === shotFeed.media);

		if (!mediaItem) {
			if (shotFeed.feedType !== FEED_TYPES.EMPTY) {
				console.log('no matching mediaItem found for => ', shotFeed);
			}
			return (<></>);
		}

		const mediaGeneratedContent = generatedContent.find(
			(gc) => gc.fkTable === 'gr_media' && gc.fkId === mediaItem.id,
		);

		const presentedItem = presentedMedia.find(
			(p) => p.mediaId === mediaItem.id,
		);

		const mediaThumbnailURL = mediaGeneratedContent && mediaItem?.type === 'hls' && mediaItem?.thumbnail_url
			? mediaItem.thumbnail_url
			: getCurrentPageThumb(
				mediaItem.media_data,
				presentedItem?.currentPage,
			);

		if (
			mediaItem.num_pages > 1
			|| IMG_TYPES.includes(mediaItem.type)
			|| VID_TYPES.includes(mediaItem.type)
			|| DOC_TYPES.includes(mediaItem.type)
			// eslint-disable-next-line eqeqeq
			|| mediaItem.type == 'hls'
		) {
			return (
				<VideoThumbnail
					key={mediaItem.id}
					includeSelectionBox
					isCurrentActiveShot={isCurrentActiveShot}
					mediaOnCanvasIds={mediaOnCanvasIds}
					shotFeed={shotFeed}
					mediaName={mediaItem?.custom_name ?? mediaItem?.original_name ?? ''}
				>
					<>
						<Thumbnail thumbnail={mediaThumbnailURL} />
					</>
				</VideoThumbnail>
			);
		}

		if (AUDIO_TYPES.includes(mediaItem.type)) {
			return (
				<VideoThumbnail
					key={mediaItem.id}
					includeSelectionBox
					isCurrentActiveShot={isCurrentActiveShot}
					mediaOnCanvasIds={mediaOnCanvasIds}
					shotFeed={shotFeed}
					mediaName={mediaItem?.custom_name ?? mediaItem?.original_name ?? ''}
				>
					<div style={{ paddingTop: '26px' }}>
						<AudioThumbnail />
					</div>
				</VideoThumbnail>
			);
		}

		return null;
	}, [
		media,
		presentedMedia,
		isCurrentActiveShot,
		mediaOnCanvasIds,
	]);

	const getSrtStreamThumb = (shotFeed: ShotFeed) => {
		const srtStream = standaloneSrtStreams.find((s) => s.id === shotFeed.srtStreamId);
		if (!srtStream) return (<></>);
		return (
			<div className='shot-drawer-thumbnail'>
				<div className="video-thumb">
					<SrtStreamThumbnail
						srtStream={srtStream}
						location={ThumbnailLocation.ShotTab}
					/>
				</div>
			</div>
		);
	};

	const getOnsiteLocationThumbs = (shotFeed: ShotFeed) => {
		const onsiteLocation = onsiteLocations.find((s) => s.id === shotFeed.onsiteLocationId);
		if (!onsiteLocation) return (<></>);
		const locationStreams = onsiteLocationStreams.get(onsiteLocation.id);
		if (locationStreams?.length) {
			return (
				locationStreams.map((locationStream, i) => (
					<div key={`${onsiteLocation.id}-${i}`}>
						<div className='shot-drawer-thumbnail'>
							<div className="video-thumb">
								<OnsiteLocationThumbnail
									key={i}
									onsiteLocation={onsiteLocation}
									location={ThumbnailLocation.ShotTab}
									locationStream={locationStream}
								/>
							</div>
						</div>
					</div>
				))
			);
		}
		const srtStream = onsiteSrtStreams.find(
			(s) => s.status === SrtStreamStatus.active && s.id === onsiteLocation.srtId,
		);
		if (srtStream) {
			return (
				<OnsiteLocationThumbnail
					onsiteLocation={onsiteLocation}
					location={ThumbnailLocation.ShotTab}
					srtStream={srtStream}
				/>
			);
		}
		return (
			<div key={onsiteLocation.id}>
				<div className='shot-drawer-thumbnail'>
					<div className="video-thumb">
						<OnsiteLocationThumbnail
							onsiteLocation={onsiteLocation}
							location={ThumbnailLocation.ShotTab}
						/>
					</div>
				</div>
			</div>
		);
	};

	const getVideoThumbs = useMemo(() => shotFeeds.map((shotFeed) => {
		let feed = <></> as React.ReactElement | null;
		let key;
		if (shotFeed.feedType === FEED_TYPES.PRESENTER) {
			feed = getPresenterThumb(shotFeed);
			key = shotFeed.presenter;
		}

		if (shotFeed.feedType === FEED_TYPES.SCREENSHARE) {
			feed = getScreenshareThumb(shotFeed);
			key = shotFeed.screenshare;
		}

		if (shotFeed.feedType === FEED_TYPES.MEDIA
		|| shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO) {
			feed = getMediaThumb(shotFeed);
			key = shotFeed.media;
		}

		if (shotFeed.feedType === FEED_TYPES.SIP_CALL) {
			feed = getSipCallThumb(shotFeed);
			key = shotFeed.sipStreamId;
		}

		if (shotFeed.feedType === FEED_TYPES.SRT_STREAM) {
			feed = getSrtStreamThumb(shotFeed);
			key = shotFeed.srtStreamId;
		}

		if (shotFeed.feedType === FEED_TYPES.ONSITE_LOCATION) {
			return getOnsiteLocationThumbs(shotFeed);
		}

		return (feed && key) ? <div key={key}>{feed}</div> : null;
	}), [
		shotFeeds,
		getPresenterThumb,
		getScreenshareThumb,
		getMediaThumb,
	]);

	return (<>
		<label className="shots-label">Feeds & Media</label>

		<div className={'video-thumbs'} style={{ display: 'grid' }}>
			{getVideoThumbs}
		</div>
	</>);
}
